import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Camera, Trash2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import Avatar from '@/components/ui/Avatar'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/toast'

type PhotoOwner = { name?: string | null; avatar_url?: string | null; has_custom_avatar?: boolean }

/**
 * Profile photo block shared by the staff/admin Profile page, the customer
 * Settings page and the admin's Edit team member modal. With no props it is
 * the viewer's own photo through /profile/avatar; the admin passes the
 * member, their endpoint and a refresh of the team list. "Remove" only shows
 * for a photo we stored ourselves (a Google avatar has nothing to delete and
 * quietly reappears on the next Google sign-in), and asks first because the
 * file is deleted.
 */
export default function AvatarUploader({
  user: owner,
  endpoint = '/profile/avatar',
  onChanged,
}: {
  user?: PhotoOwner
  endpoint?: string
  onChanged?: () => Promise<unknown> | void
}) {
  const { user: me, refresh } = useAuth()
  const user = owner ?? me
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const changed = onChanged ?? refresh

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('avatar', file)
      return (await api.post(endpoint, form)).data
    },
    onSuccess: async (data) => {
      await changed()
      toast.success(data.message ?? 'Photo updated')
    },
    onError: (err) => toast.error('Upload failed', errorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async () => (await api.delete(endpoint)).data,
    onSuccess: async (data) => {
      await changed()
      setConfirmRemove(false)
      toast.success(data.message ?? 'Photo removed')
    },
    onError: (err) => {
      setConfirmRemove(false)
      toast.error('Remove failed', errorMessage(err))
    },
  })

  return (
    // Avatar left, controls right - on phones too, never stacked.
    <div className="flex items-center gap-4">
      <Avatar user={user} className="size-16 shrink-0 bg-primary text-lg font-semibold text-white" />
      <div className="min-w-0 flex-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) upload.mutate(file)
            e.target.value = ''
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" loading={upload.isPending} onClick={() => inputRef.current?.click()}>
            <Camera className="size-4" /> {user?.avatar_url ? 'Change photo' : 'Upload photo'}
          </Button>
          {user?.has_custom_avatar && (
            <Button variant="outline" onClick={() => setConfirmRemove(true)}>
              <Trash2 className="size-4" /> Remove
            </Button>
          )}
        </div>
        <p className="mt-1.5 text-xs text-muted">JPG, PNG or WebP, up to 2 MB.</p>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => remove.mutate()}
        title="Remove this photo?"
        confirmLabel="Remove photo"
        variant="danger"
        loading={remove.isPending}
      >
        The photo is deleted and {owner ? `${owner.name ?? 'they'} goes` : 'you go'} back to initials. A new one can be
        uploaded at any time.
      </ConfirmDialog>
    </div>
  )
}
